import React, { useState } from 'react';
import { Alert, Box, Button, Grid, Paper, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { AccountService } from '../types/account';
import apiService from '../services/api';
import SupaBuildingUnitPicker, { type SupaBuildingSelection } from './SupaBuildingUnitPicker';

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ bgcolor: '#1e3a5c', px: 2, py: 0.75, mx: -3, mt: -3, mb: 2.5, borderRadius: '4px 4px 0 0' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: '#ffffff' }}>
        {children}
      </Typography>
    </Box>
  );
}

function StatField({ label, value }: { label: string; value: string }) {
  return (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.25 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Grid>
  );
}

type Props = {
  service: AccountService;
};

/** Building + full service address assignment for a Supa Networks service —
 * pulled from the Supa serviced-buildings/service-qualification API so
 * addressing is always sourced from Supa, not hand-typed. Stored in
 * type_specific_data (building_place_id/building_name/building_key/
 * service_address); building_key is what a future outage-notification
 * "select a building, notify everyone in it" feature would query on.
 *
 * Selecting a building/unit only stages it (pendingSelection) — nothing is
 * written until "Save changes" is clicked, so the save action is explicit. */
export default function SupaBuildingAddress({ service }: Props) {
  const tsd = (service.type_specific_data || {}) as Record<string, string>;
  const [buildingName, setBuildingName] = useState(tsd.building_name || '');
  const [buildingKey, setBuildingKey] = useState(tsd.building_key || '');
  const [serviceAddress, setServiceAddress] = useState(tsd.service_address || '');
  const [editing, setEditing] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<SupaBuildingSelection | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEditing = () => {
    setError(null);
    setPendingSelection(null);
    setEditing(true);
  };

  const cancelEditing = () => {
    setPendingSelection(null);
    setEditing(false);
  };

  const handleSave = async () => {
    if (!pendingSelection) return;
    setSaving(true);
    setError(null);
    try {
      const merged = {
        ...(service.type_specific_data || {}),
        building_place_id: pendingSelection.building.place_id || '',
        building_name: pendingSelection.building.buildingName,
        building_key: pendingSelection.buildingKey,
        service_address: pendingSelection.address,
      };
      await apiService.updateService(service.id, { type_specific_data: merged });
      setBuildingName(pendingSelection.building.buildingName);
      setBuildingKey(pendingSelection.buildingKey);
      setServiceAddress(pendingSelection.address);
      setPendingSelection(null);
      setEditing(false);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Could not save building/address');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 2 }}>
      <SectionLabel>Building &amp; Service Address</SectionLabel>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {editing ? (
        pendingSelection ? (
          <Box>
            <Alert severity="info" icon={<CheckCircleIcon fontSize="small" />} sx={{ mb: 2, py: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>{pendingSelection.building.buildingName}</Typography>
              <Typography variant="body2">{pendingSelection.address}</Typography>
            </Alert>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="contained" disabled={saving} onClick={handleSave}>
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
              <Button size="small" disabled={saving} onClick={() => setPendingSelection(null)}>
                Pick a different one
              </Button>
              <Button size="small" color="inherit" disabled={saving} onClick={cancelEditing}>
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <SupaBuildingUnitPicker
              value=""
              initialBuildingKey={buildingKey || undefined}
              onSelect={setPendingSelection}
              onClear={cancelEditing}
            />
            <Button size="small" color="inherit" sx={{ mt: 1 }} onClick={cancelEditing}>
              Cancel
            </Button>
          </Box>
        )
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <StatField label="Building" value={buildingName || '—'} />
            <StatField label="Service address" value={serviceAddress || '—'} />
          </Grid>
          <Button size="small" variant="outlined" onClick={startEditing}>
            {buildingName ? 'Change building' : 'Assign building'}
          </Button>
        </>
      )}
    </Paper>
  );
}
